import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Guards against losing unsaved work when `when` is true. Covers both
 * exit paths:
 *   - tab close / reload → browser-native confirm (beforeunload)
 *   - in-app navigation (links, useNavigate) → react-router blocker +
 *     the in-app confirm dialog below
 *
 * Requires a data router (createBrowserRouter / RouterProvider) — that's
 * what makes useBlocker available. Render it anywhere inside the page
 * whose work needs protecting; it returns null until a navigation is
 * actually blocked.
 */
export function UnsavedChangesGuard({
  when,
  message = 'Du har osparade ändringar som går förlorade om du lämnar sidan.',
}: {
  when: boolean;
  message?: string;
}) {
  useEffect(() => {
    if (!when) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [when]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when && currentLocation.pathname !== nextLocation.pathname,
  );

  // If the work was saved while a block was pending, let it through.
  useEffect(() => {
    if (blocker.state === 'blocked' && !when) blocker.proceed();
  }, [blocker, when]);

  if (blocker.state !== 'blocked') return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex w-full max-w-sm flex-col gap-4 rounded bg-white p-5 shadow-xl">
        <div>
          <h3 className="text-sm font-semibold">Lämna sidan?</h3>
          <p className="mt-1 text-sm text-stone-600">{message}</p>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => blocker.reset()}
            className="rounded px-3 py-1.5 text-xs font-medium text-stone-600 hover:text-stone-900"
          >
            Stanna kvar
          </button>
          <button
            type="button"
            onClick={() => blocker.proceed()}
            className="rounded bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
          >
            Lämna utan att spara
          </button>
        </div>
      </div>
    </div>
  );
}
